/**
 * HelpPopover — small "?" icon trigger that opens an anchored popover
 * with page-specific FAQ entries.
 *
 * Designed to drop in next to any PageHeader title (or wherever a
 * page wants to expose contextual help). Each consumer passes in its
 * own list of FAQ entries — there's no shared knowledge base, so a
 * page only shows what's relevant to it.
 *
 * The popover is rendered via a React portal into document.body so
 * it always escapes any ancestor's stacking context. Without the
 * portal the popover could fall behind sticky table headers, sticky
 * strike columns, or any element whose CSS coincidentally beats the
 * popover's z-index — which is exactly what was happening on
 * /app/options where the heatmap covered the popover. Position
 * (top/left in viewport coordinates) is computed from the trigger's
 * bounding rect at open time and on scroll/resize.
 *
 * Behavior:
 *   • Click the ? → popover opens, anchored below the icon
 *   • Click outside the popover → closes
 *   • Press Escape → closes (and returns focus to the trigger)
 *   • Window resize / scroll → repositions to follow the trigger
 *   • On screens < 720 px the popover promotes to a bottom sheet
 *     (full-width, pinned to bottom)
 *
 * Click-only — never auto-opens. Visitors discover it by curiosity,
 * not by being interrupted.
 */

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** All element types that can receive keyboard focus. */
const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export interface FaqEntry {
  q: string;
  /**
   * Body content. Strings render as paragraphs; ReactNodes render as-is
   * so callers can pass in <code>, lists, etc.
   */
  a: React.ReactNode;
}

interface HelpPopoverProps {
  title?: string;
  entries: FaqEntry[];
  /**
   * Aria label for the trigger button. Defaults to "Open help".
   * Override when the page has multiple help triggers and screen
   * readers need disambiguation ("Open heatmap help" etc).
   */
  ariaLabel?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
  /** True when there isn't enough room to the right; flip the bubble's
   * tail/anchor leftward so it doesn't clip the viewport. */
  flipLeft: boolean;
}

// Width matches the popover panel — keep in sync with the CSS rule.
const POPOVER_W = 420;
const MOBILE_BP = 720;

function computePosition(triggerRect: DOMRect): PopoverPosition {
  const margin = 12;
  const desiredLeft = triggerRect.right + margin - POPOVER_W;
  const minLeft = 12;
  const maxLeft = (typeof window !== 'undefined' ? window.innerWidth : 1024) - POPOVER_W - 12;
  const left = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
  return {
    top: triggerRect.bottom + 10,
    left,
    flipLeft: desiredLeft < minLeft,
  };
}

const HelpPopover: React.FC<HelpPopoverProps> = ({
  title = 'How to read this page',
  entries,
  ariaLabel = 'Open help',
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  // Track the element that was focused before opening so we can restore
  // it on close — important for keyboard / screen-reader users.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    // Return focus to wherever it was before the popover opened.
    // Falls back to the trigger button if nothing was saved.
    const target = previousFocusRef.current ?? triggerRef.current;
    target?.focus();
    previousFocusRef.current = null;
  }, []);

  const reposition = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    setPos(computePosition(t.getBoundingClientRect()));
  }, []);

  // Compute initial position synchronously after open so the popover
  // never paints at (0,0) before the layout effect fires.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Outside-click + Escape dismiss + reposition on scroll/resize.
  // Focus trap + body scroll lock (mobile sheet).
  // Listeners attach only while open so we don't pay the cost on
  // every page mount.
  useEffect(() => {
    if (!open) return;

    // ── Save focus + move it into the dialog ──────────────────────
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    // Defer so the portal has painted and the popover is in the DOM.
    const focusTimer = setTimeout(() => {
      if (!popoverRef.current) return;
      const focusable = popoverRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
      if (focusable.length) focusable[0].focus();
    }, 0);

    // ── Body scroll lock on mobile bottom-sheet ───────────────────
    const mobileSheet = typeof window !== 'undefined' && window.innerWidth < MOBILE_BP;
    const prevOverflow = document.body.style.overflow;
    if (mobileSheet) document.body.style.overflow = 'hidden';

    // ── Key handler: Escape + Tab trap ────────────────────────────
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'Tab') {
        const el = popoverRef.current;
        if (!el) return;
        const focusable = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(
          n => !n.closest('[inert]') && !n.hasAttribute('disabled'),
        );
        if (focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          // Shift+Tab from first → wrap to last
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          // Tab from last → wrap to first
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    // ── Outside-click dismiss ─────────────────────────────────────
    const onClick = (e: MouseEvent) => {
      if (!popoverRef.current || !triggerRef.current) return;
      const t = e.target as Node;
      if (popoverRef.current.contains(t) || triggerRef.current.contains(t)) return;
      close();
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    window.addEventListener('resize', reposition);
    // Scroll on any ancestor — capture phase catches them all.
    window.addEventListener('scroll', reposition, true);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      // Restore body scroll.
      if (mobileSheet) document.body.style.overflow = prevOverflow;
    };
  }, [open, close, reposition]);

  // Decide bottom-sheet vs popover based on viewport width.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BP;

  const popoverNode = open && (typeof document !== 'undefined') ? createPortal(
    <div
      ref={popoverRef}
      className={`gv-help-popover${isMobile ? ' gv-help-popover--sheet' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      style={
        isMobile || !pos ? undefined : {
          position: 'fixed',
          top: `${pos.top}px`,
          left: `${pos.left}px`,
        }
      }
    >
      <header className="gv-help-head">
        <h3 id={headingId} className="gv-help-title">{title}</h3>
        <button
          type="button"
          className="gv-help-close"
          aria-label="Close help"
          onClick={close}
        >
          ×
        </button>
      </header>
      <div className="gv-help-body">
        {entries.map((entry, i) => (
          <details key={i} className="gv-help-entry" open={i === 0}>
            <summary>{entry.q}</summary>
            <div className="gv-help-answer">
              {typeof entry.a === 'string' ? <p>{entry.a}</p> : entry.a}
            </div>
          </details>
        ))}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <span className="gv-help">
      <button
        ref={triggerRef}
        type="button"
        className="gv-help-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(o => !o)}
      >
        ?
      </button>
      {popoverNode}
    </span>
  );
};

export default HelpPopover;
